import { Component, type ErrorInfo, type ReactNode } from "react";
import { Sentry } from "@/lib/monitoring";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  eventId?: string;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Always log to console so dev sees the stack immediately.
    console.error("[ErrorBoundary]", error, info);
    // Forward to Sentry when configured (safe no-op otherwise).
    const eventId = Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
    this.setState({ eventId });
  }

  private handleReset = () => {
    this.setState({ hasError: false, eventId: undefined });
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-dvh flex items-center justify-center bg-background px-6">
        <div className="max-w-md text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-steel-2 font-semibold mb-4">
            Something went wrong
          </p>
          <h1 className="text-[clamp(1.8rem,2.8vw,2.5rem)] font-semibold text-ink mb-4">
            We hit an unexpected error
          </h1>
          <p className="text-base text-muted-foreground mb-8">
            The team has been notified. Try reloading, or head back to the homepage.
          </p>
          {this.state.eventId && (
            <p className="text-sm text-muted-foreground mb-6">
              Reference: <span className="font-mono">{this.state.eventId}</span>
            </p>
          )}
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => window.location.reload()}>
              Reload
            </Button>
            <Button variant="primary" onClick={this.handleReset}>
              Back to home
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
